/**
 * Rehype plugin: promote a table's lead-in paragraph to a heading.
 *
 * The agent habitually introduces a table with a short colon-terminated line — "Revenue by
 * market, 2021-2025:" — and markdown has no way to say that the line belongs to the table
 * rather than to the prose above it. Rendered as body text it reads as a sentence that stops
 * mid-thought; rendered as a heading it does what the author meant, and the reveal plugin then
 * groups it with the table it introduces instead of stranding it at the end of the previous
 * section.
 *
 * The pattern is deliberately narrow, because a false positive silently promotes a real
 * sentence to a heading: a *short* paragraph, a *single* text run (any inline markup and the
 * author was writing prose), a trailing colon, and a table as the very next block. All four, or
 * nothing happens.
 *
 * Runs for every answer, not just the ones the reveal is switched on for — a lead-in is a
 * lead-in whether or not its arrival is being staggered.
 */

import { HastNode } from './MdTable';

export type CaptionRoot = {
  type: 'root';
  children: HastNode[];
};

/**
 * Long enough for the lead-ins the agent actually writes, short enough that a sentence ending
 * in a colon — a list introduction, a definition — is out of range. Measured on the stripped
 * text, so the colon itself does not count against it.
 */
export const MAX_CAPTION_LENGTH = 90;

/** The level the caption renders at: `mdH4`, the serif 18px that Charlie gives table heads. */
const CAPTION_TAG = 'h4';

/** @desc Marks a heading this plugin promoted, so a renderer can tell it from an authored one. */
export const TABLE_CAPTION_ATTRIBUTE = 'data-table-caption';

const soleText = (node: HastNode): HastNode | undefined => {
  const [only, ...rest] = node.children ?? [];
  return only?.type === 'text' && rest.length === 0 ? only : undefined;
};

/**
 * The next *block* after `index`, skipping the whitespace-only text nodes mdast-util-to-hast
 * leaves between siblings. Reading `children[index + 1]` directly finds a "\n" and concludes
 * that nothing follows the paragraph — which is how this pattern silently never fires.
 */
function nextBlock(children: HastNode[], index: number): HastNode | undefined {
  for (const node of children.slice(index + 1)) {
    if (node.type === 'text' && (node.value ?? '').trim() === '') continue;
    return node;
  }
  return undefined;
}

/** Whether `node` introduces `next`: a short, plain, colon-terminated line ahead of a table. */
export function isTableCaption(node: HastNode, next: HastNode | undefined): boolean {
  if (node.type !== 'element' || node.tagName !== 'p') return false;
  if (next?.tagName !== 'table') return false;

  const text = soleText(node);
  if (!text) return false;

  const value = (text.value ?? '').trim();
  return value.endsWith(':') && value.slice(0, -1).trim().length <= MAX_CAPTION_LENGTH;
}

export function rehypeTableCaptions() {
  return (tree: CaptionRoot): void => {
    tree.children.forEach((node, index) => {
      if (!isTableCaption(node, nextBlock(tree.children, index))) return;

      const text = soleText(node);
      if (!text) return;

      // The colon is punctuation joining two blocks, and the join is now structural
      node.tagName = CAPTION_TAG;
      node.properties = { ...node.properties, [TABLE_CAPTION_ATTRIBUTE]: 'true' };
      text.value = (text.value ?? '').trim().slice(0, -1).trim();
    });
  };
}
